import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, formatINR, useAuth, formatISTDate } from '../lib/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, User, Phone, MapPin, Calendar, TrendingUp, FileText, Download, Eye, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerProfilePage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.getCustomerProfile(customerId);
        setData(res.data);
      } catch (err) {
        toast.error('Customer not found');
        navigate('/quotations');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [customerId, navigate]);

  const formatDate = (iso) => formatISTDate(iso);

  const handleDownloadPdf = async (quotationId, customerName) => {
    try {
      const res = await api.downloadPdf(quotationId, false);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quotation_${customerName.replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  if (!data || !data.customer) {
    return (
      <div className="flex items-center justify-center py-20 text-[#B93838]">
        Customer not found
      </div>
    );
  }

  const { customer, quotations, stats } = data;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="customer-profile-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-[#1A1C16]">Customer Profile</h1>
          <p className="text-xs text-muted-foreground">View all quotations for this customer</p>
        </div>
      </div>

      {/* Customer Info Card */}
      <Card className="border-[#E2E6D9] mb-6" data-testid="customer-info-card">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-[#556B2F]/10 flex items-center justify-center">
                <User className="w-8 h-8 text-[#556B2F]" />
              </div>
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <h2 className="text-2xl font-bold text-[#1A1C16]" data-testid="customer-name">{customer.name}</h2>
                {customer.mobile && (
                  <div className="flex items-center gap-2 text-muted-foreground mt-1">
                    <Phone className="w-4 h-4" />
                    <span data-testid="customer-mobile">{customer.mobile}</span>
                  </div>
                )}
              </div>
              {customer.address && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>{customer.address}</span>
                </div>
              )}
              {customer.gst_no && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Building2 className="w-4 h-4" />
                  <span>GST: {customer.gst_no}</span>
                </div>
              )}
            </div>
            <div className="flex gap-4 md:gap-8">
              <div className="text-center">
                <p className="text-3xl font-bold text-[#556B2F]" data-testid="total-quotations">{stats.total_quotations}</p>
                <p className="text-xs text-muted-foreground">Quotations</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1A1C16]" data-testid="total-value">{formatINR(stats.total_value)}</p>
                <p className="text-xs text-muted-foreground">Total Value</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      {stats.first_quotation && (
        <div className="flex items-center gap-4 mb-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>First: {formatDate(stats.first_quotation)}</span>
          </div>
          <Separator orientation="vertical" className="h-4" />
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            <span>Latest: {formatDate(stats.last_quotation)}</span>
          </div>
        </div>
      )}

      {/* Quotations List */}
      <Card className="border-[#E2E6D9]" data-testid="quotations-list">
        <CardHeader className="border-b border-[#E2E6D9] py-4">
          <CardTitle className="text-sm font-bold uppercase tracking-wide">Quotation History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {quotations.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <FileText className="w-8 h-8 mr-2 opacity-50" />
              No quotations found
            </div>
          ) : (
            <div className="divide-y divide-[#E2E6D9]">
              {quotations.map((q) => (
                <div key={q.id} className="flex items-center justify-between gap-2 p-3 sm:p-4 hover:bg-[#F9FAF7] transition-colors" data-testid={`quotation-row-${q.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                      <span className="font-mono-data font-semibold text-[#556B2F] text-sm sm:text-base">{q.quotation_number}</span>
                      <Badge variant="outline" className="text-[10px] sm:text-xs">{q.items?.length || 0} items</Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] sm:text-xs text-muted-foreground">
                      <span className="whitespace-nowrap">{formatDate(q.created_at)}</span>
                      {q.subject && <span className="truncate min-w-0 max-w-[180px] sm:max-w-xs">{q.subject}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-3 flex-shrink-0">
                    <span className="text-sm sm:text-lg font-bold font-mono-data whitespace-nowrap">{formatINR(q.grand_total)}</span>
                    <div className="flex gap-0.5 sm:gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 sm:h-9 sm:w-9 p-0" onClick={() => navigate(`/quotations/${q.id}`)} title="View" data-testid={`view-quotation-row-${q.id}`}>
                        <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 sm:h-9 sm:w-9 p-0" onClick={() => handleDownloadPdf(q.id, customer.name)} title="Download PDF" data-testid={`download-quotation-row-${q.id}`}>
                        <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
